import { useMemo, useEffect } from 'react';
import { GroupBase } from 'react-select';
import { ShippingMethodsResponseDto } from '@tiendanube/common';
import { LocationResponseDto } from '@tiendanube/common/src/domains/shipping/locations/LocationResponseDto';
import { Status } from '@tiendanube/common/src/enums';
import {
  InterfaceSelectOption,
  InterfaceSelectOptionGroup,
} from '@tiendanube/components';
import { OptionType } from 'commons/components/MultiSelect/multiSelectStyleConfig';
import {
  FiltersParamsType,
  FilterStatusEnum,
  status,
  fulfillmentStatus,
  paymentStatus,
  orderOrigin,
  PIPE_SEPARATOR,
  COMMA_SEPARATOR,
  FilterOriginEnum,
} from 'domains/Orders/Orders/ordersService';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import { useGetShippingMethods } from 'domains/Shipping/DeliveryMethods/hooks';
import { EMPTY_SHIPPING_METHODS } from 'domains/Shipping/DeliveryMethods/hooks/useGetShippingMethods/useGetShippingMethods';

function buildFulfillmentStatusOptions(
  t: (key: string) => string,
): InterfaceSelectOptionGroup[] {
  return fulfillmentStatus
    .filter((statusValue) => statusValue !== FilterStatusEnum.ALL)
    .map((statusValue) => ({
      value: statusValue,
      label: t(`filters.fulfillment.${statusValue}`),
    }));
}

function buildPaymentOptions(
  t: (key: string) => string,
): InterfaceSelectOptionGroup[] {
  return paymentStatus
    .filter((statusValue) => statusValue !== FilterStatusEnum.ALL)
    .map((statusValue) => ({
      value: statusValue,
      label: t(`filters.payment.${statusValue}`),
    }));
}

// Generic helper to parse multi-select values from string
const parseMultiSelectValues = <T>(
  value: string,
  separator: string,
  options: T[],
  findFn: (option: T, value: string) => boolean,
  labelFn: (option: T | undefined, value: string) => string,
): OptionType[] => {
  if (!value || value === '') return [];

  return value
    .split(separator)
    .map((v) => v.trim())
    .filter((v) => v !== '')
    .map((v) => {
      const found = options.find((opt) => findFn(opt, v));
      return {
        value: v,
        label: labelFn(found, v),
      };
    });
};

// Helper functions for parseMultiSelectValues
const findOptionByValue = <T extends { value: unknown }>(
  option: T,
  value: string,
) => option.value === value;

const findOptionById = <T extends { id: string }>(option: T, value: string) =>
  option.id === value;

const findOptionByCode = <T extends { code: string }>(
  option: T,
  value: string,
) => option.code === value;

const getLabel = <T extends { label?: string }>(
  option: T | undefined,
  fallback: string,
) => option?.label || fallback;

const getName = <T extends { name?: string }>(
  option: T | undefined,
  fallback: string,
) => option?.name || fallback;

interface UseFilterOptionsSelectsParams {
  values: Partial<FiltersParamsType>;
  locations?: LocationResponseDto[];
  paymentMethodOptions: InterfaceSelectOption[];
  paymentProviderOptions: InterfaceSelectOption[];
}

interface UseFilterOptionsSelectsResult {
  defaultStatus: OptionType[];
  statusOptionsForMultiSelect: OptionType[];
  defaultShippingMethod: OptionType[];
  shippingMethodOptionsForMultiSelect: GroupBase<OptionType>[];
  shippingMethods: ShippingMethodsResponseDto;
  defaultLocation: OptionType[];
  locationOptionsForMultiSelect: OptionType[];
  defaultFulfillmentStatus: OptionType[];
  fulfillmentStatusOptions: InterfaceSelectOptionGroup[];
  defaultPaymentStatus: OptionType[];
  paymentOptions: InterfaceSelectOptionGroup[];
  defaultPaymentMethod: OptionType[];
  paymentMethodOptionsForMultiSelect: OptionType[];
  defaultPaymentProvider: OptionType[];
  paymentProviderOptionsForMultiSelect: OptionType[];
  defaultOrigin: OptionType[];
  originOptionsForMultiSelect: OptionType[];
}

export function useFilterOptionsSelects({
  values,
  locations,
  paymentMethodOptions,
  paymentProviderOptions,
}: UseFilterOptionsSelectsParams): UseFilterOptionsSelectsResult {
  const t = useTranslationOrders();
  const {
    shippingMethods: rawShippingMethods,
    fetchShippingMethods,
    status: shippingMethodsStatus,
  } = useGetShippingMethods();
  const shippingMethods = rawShippingMethods ?? EMPTY_SHIPPING_METHODS;

  useEffect(() => {
    if (shippingMethodsStatus.isIdle) {
      fetchShippingMethods();
    }
  }, [shippingMethodsStatus, fetchShippingMethods]);

  const statusOptions: InterfaceSelectOptionGroup[] = useMemo(
    () =>
      status.map((statusValue) => ({
        value: statusValue,
        label: t(`filters.status.${statusValue}`),
      })),
    [t],
  );

  const defaultStatus = useMemo(() => {
    if (!values.status || typeof values.status !== 'string') {
      return [{ value: Status.OPEN, label: t('filters.status.open') }];
    }

    const statusValue: string = values.status;

    if (statusValue === FilterStatusEnum.ALL) {
      return [];
    }

    if (statusValue === FilterStatusEnum.EMPTY) {
      return [{ value: Status.OPEN, label: t('filters.status.open') }];
    }

    return statusValue
      .split(',')
      .filter((option) => option.trim() !== '')
      .map((option) => ({
        label: t(`filters.status.${option}`),
        value: option,
      }));
  }, [values.status, t]);

  const statusOptionsForMultiSelect: OptionType[] = useMemo(
    () =>
      statusOptions
        .filter(
          (opt): opt is { value: string; label: string } =>
            'value' in opt && opt.value !== FilterStatusEnum.ALL,
        )
        .map((opt) => ({
          value: opt.value,
          label: opt.label,
        })),
    [statusOptions],
  );

  const defaultShippingMethod = useMemo(() => {
    if (!values.shippingMethod || values.shippingMethod === '') return [];

    const flatMethods = Object.values(shippingMethods).flat();
    return parseMultiSelectValues(
      values.shippingMethod,
      COMMA_SEPARATOR,
      flatMethods,
      findOptionByCode,
      getName,
    );
  }, [values.shippingMethod, shippingMethods]);

  const shippingMethodOptionsForMultiSelect: GroupBase<OptionType>[] = useMemo(
    () =>
      Object.keys(shippingMethods)
        .filter(
          (key) =>
            Array.isArray(shippingMethods[key]) &&
            shippingMethods[key].length > 0,
        )
        .map((key) => ({
          label: t(`filters.shippingMethod.${key}`),
          options: shippingMethods[key].map((method) => ({
            value: method.code,
            label: method.name,
          })),
        })),
    [shippingMethods, t],
  );

  const defaultLocation = useMemo(() => {
    if (!locations || !values.location || values.location === '') return [];

    return parseMultiSelectValues(
      values.location,
      COMMA_SEPARATOR,
      locations,
      findOptionById,
      getName,
    );
  }, [values.location, locations]);

  const locationOptionsForMultiSelect: OptionType[] = useMemo(
    () =>
      locations
        ? locations.map((location) => ({
            value: location.id,
            label: location.name,
          }))
        : [],
    [locations],
  );

  const defaultFulfillmentStatus = useMemo(() => {
    if (!values.fulfillmentStatus) return [];
    return values.fulfillmentStatus.split(',').map((option) => ({
      label: t(`filters.fulfillment.${option}`),
      value: option,
    }));
  }, [values.fulfillmentStatus, t]);

  const fulfillmentStatusOptions: InterfaceSelectOptionGroup[] =
    buildFulfillmentStatusOptions(t);

  const defaultPaymentStatus = useMemo(() => {
    if (!values.paymentStatus) return [];
    return values.paymentStatus.split(',').map((option) => ({
      label: t(`filters.payment.${option}`),
      value: option,
    }));
  }, [values.paymentStatus, t]);

  const paymentOptions: InterfaceSelectOptionGroup[] = buildPaymentOptions(t);

  const defaultPaymentMethod = useMemo(() => {
    if (!values.paymentMethods || values.paymentMethods === '') return [];

    return parseMultiSelectValues(
      values.paymentMethods,
      COMMA_SEPARATOR,
      paymentMethodOptions,
      findOptionByValue,
      getLabel,
    );
  }, [values.paymentMethods, paymentMethodOptions]);

  const paymentMethodOptionsForMultiSelect: OptionType[] = useMemo(
    () =>
      paymentMethodOptions
        .filter((opt) => opt.value !== '')
        .map((method) => ({
          value: method.value as string,
          label: method.label,
        })),
    [paymentMethodOptions],
  );

  const defaultPaymentProvider = useMemo(() => {
    if (!values.paymentProvider || values.paymentProvider === '') return [];

    return parseMultiSelectValues(
      values.paymentProvider,
      PIPE_SEPARATOR,
      paymentProviderOptions,
      findOptionByValue,
      getLabel,
    );
  }, [values.paymentProvider, paymentProviderOptions]);

  const paymentProviderOptionsForMultiSelect: OptionType[] = useMemo(
    () =>
      paymentProviderOptions
        .filter((opt) => opt.value !== '')
        .map((provider) => ({
          value: provider.value as string,
          label: provider.label,
        })),
    [paymentProviderOptions],
  );

  const defaultOrigin = useMemo(() => {
    if (
      !values.origin ||
      values.origin === '' ||
      values.origin === FilterOriginEnum.ALL
    )
      return [];
    return values.origin
      .split(',')
      .filter((option) => option.trim() !== '')
      .map((origin) => ({
        value: origin,
        label: t(`filters.origin.${origin}`),
      }));
  }, [values.origin, t]);

  const originOptionsForMultiSelect: OptionType[] = useMemo(
    () =>
      orderOrigin
        .filter((origin) => origin !== FilterOriginEnum.ALL)
        .map((origin) => ({
          value: origin,
          label: t(`filters.origin.${origin}`),
        })),
    [t],
  );

  return {
    defaultStatus,
    statusOptionsForMultiSelect,
    defaultShippingMethod,
    shippingMethodOptionsForMultiSelect,
    shippingMethods,
    defaultLocation,
    locationOptionsForMultiSelect,
    defaultFulfillmentStatus,
    fulfillmentStatusOptions,
    defaultPaymentStatus,
    paymentOptions,
    defaultPaymentMethod,
    paymentMethodOptionsForMultiSelect,
    defaultPaymentProvider,
    paymentProviderOptionsForMultiSelect,
    defaultOrigin,
    originOptionsForMultiSelect,
  };
}
